import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { SectionHeader } from '@/components/SectionHeader';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Trophy, Medal, Crown, Star, Loader2, Flame, Zap, Gift, Calendar } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rankStyles = {
  1: { medal: 'from-amber-400 to-yellow-500', bg: 'from-amber-500/25 to-yellow-500/15', border: 'border-amber-500/50', text: 'text-amber-400', icon: Crown },
  2: { medal: 'from-slate-300 to-gray-400',  bg: 'from-slate-300/15 to-gray-400/10',   border: 'border-slate-400/40', text: 'text-slate-300', icon: Medal },
  3: { medal: 'from-orange-500 to-amber-600', bg: 'from-orange-600/20 to-amber-700/15', border: 'border-orange-600/40', text: 'text-orange-400', icon: Medal },
};

function Avatar({ src, name, size = 32, glow = false }) {
  const initials = (name || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();
  const style = { width: size, height: size };
  if (src) {
    return <img src={src} alt={name} style={style}
      className={`rounded-full object-cover ${glow ? 'ring-2 ring-amber-400/50 shadow-lg shadow-amber-500/30' : ''}`} />;
  }
  return (
    <div style={style}
      className={`rounded-full bg-gradient-to-br from-[#E63946] to-[#F97316] flex items-center justify-center text-white font-bold ${glow ? 'ring-2 ring-amber-400/50 shadow-lg shadow-amber-500/30' : ''}`}>
      <span style={{ fontSize: size * 0.36 }}>{initials}</span>
    </div>
  );
}

export default function LeaderboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null); // null = current
  const [availableMonths, setAvailableMonths] = useState([]);
  const [deptFilter, setDeptFilter] = useState('all');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // Departments to filter by (lowercase match)
  const DEPARTMENTS = [
    { key: 'all',              label: 'All Departments', color: '#9CA3AF' },
    { key: 'marketing',        label: 'Marketing',       color: '#E63946' },
    { key: 'sales',            label: 'Sales',           color: '#10B981' },
    { key: 'signal provider',  label: 'Signal Provider', color: '#F59E0B' },
    { key: 'ib',               label: 'IB',              color: '#3B82F6' },
    { key: 'creative',         label: 'Creative',        color: '#8B5CF6' },
    { key: 'project manager',  label: 'Project Manager', color: '#EC4899' },
    { key: 'hr',               label: 'HR',              color: '#06B6D4' },
    { key: 'finance',          label: 'Finance',         color: '#F97316' },
  ];

  const fetchMonths = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/leaderboard/months`, { headers: { Authorization: `Bearer ${token}` } });
      setAvailableMonths(res.data.months || []);
    } catch {}
  };

  const fetchLeaderboard = async (month) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = month ? `${API}/leaderboard?month=${month}` : `${API}/leaderboard`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch { toast.error('Failed to load leaderboard'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchMonths();
    fetchLeaderboard(null);
  }, []);

  const onSelectMonth = (m) => {
    setSelectedMonth(m.current ? null : m.month);
    fetchLeaderboard(m.current ? null : m.month);
  };

  if (loading || !data) return (
    <ResponsiveLayout>
      <div className="flex-1 flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" />
      </div>
    </ResponsiveLayout>
  );

  const sorted = [...(data.entries || [])]
    .filter(e => deptFilter === 'all' || (e.department || '').toLowerCase().includes(deptFilter.toLowerCase()))
    .sort((a, b) => (b.points || 0) - (a.points || 0));
  const withPoints = sorted.filter(e => e.points > 0);
  const top3 = withPoints.slice(0, 3);
  const rest = withPoints.slice(3);
  const zeroEntries = sorted.filter(e => e.points === 0);
  const maxPoints = top3[0]?.points || 1;

  const topScorer = top3[0];
  const totalParticipants = withPoints.length;
  const totalPoints = data.total_points || 0;
  const monthLabel = data.month;
  const isHistorical = !!selectedMonth;

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6">
        <SectionHeader
          icon={Trophy}
          title="Team Leaderboard"
          subtitle={`${monthLabel}${isHistorical ? ' (archived)' : ' — current month'} • Team performance rankings`}
          iconBg="bg-gradient-to-br from-amber-500 to-yellow-600 shadow-amber-500/20"
        />

        {/* Monthly Prize Banner */}
        {data.monthly_prize && (
          <div className="mb-5 p-4 rounded-xl bg-gradient-to-r from-amber-500/20 via-yellow-500/10 to-transparent border border-amber-500/40 flex items-center gap-3 animate-pulse-slow" data-testid="prize-banner">
            <div className="w-10 h-10 rounded-lg bg-amber-500/25 border border-amber-500/40 flex items-center justify-center shrink-0">
              <Gift size={20} className="text-amber-400" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-amber-400">This Month's Prize</p>
              <p className="text-sm font-semibold text-white">{data.monthly_prize}</p>
            </div>
          </div>
        )}

        {/* Month Selector Tabs */}
        {availableMonths.length > 0 && (
          <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1" data-testid="month-tabs">
            <Calendar size={14} className="text-[hsl(var(--text-muted))] shrink-0" />
            {availableMonths.map(m => (
              <button
                key={m.month}
                onClick={() => onSelectMonth(m)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap border transition-all ${
                  (m.current && !selectedMonth) || (selectedMonth === m.month)
                    ? 'bg-[#E63946]/20 border-[#E63946] text-white'
                    : 'bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:text-white'
                }`}
                data-testid={`month-tab-${m.month}`}
              >
                {m.current ? `${m.month} • Current` : m.month}
              </button>
            ))}
          </div>
        )}

        {/* Department Filter Pills */}
        <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1" data-testid="dept-filter">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--text-muted))] shrink-0">Department:</span>
          {DEPARTMENTS.map(d => (
            <button
              key={d.key}
              onClick={() => setDeptFilter(d.key)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap border transition-all ${
                deptFilter === d.key
                  ? 'text-white'
                  : 'text-[hsl(var(--text-muted))] hover:text-white bg-[hsl(var(--surface))]'
              }`}
              style={deptFilter === d.key
                ? { background: `${d.color}25`, borderColor: d.color }
                : { borderColor: 'hsl(var(--border))' }}
              data-testid={`dept-${d.key}`}
            >
              {d.label}
            </button>
          ))}
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <div className="bg-[hsl(var(--card))] border border-amber-500/20 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1"><Crown size={14} className="text-amber-400" /><p className="text-xs text-amber-400">Top Scorer</p></div>
            <p className="text-lg font-bold truncate">{topScorer?.name || '—'}</p>
            <p className="text-xs text-amber-400/70">{topScorer?.points || 0} pts</p>
          </div>
          <div className="bg-[hsl(var(--card))] border border-emerald-500/20 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1"><Star size={14} className="text-emerald-400" /><p className="text-xs text-emerald-400">Participants</p></div>
            <p className="text-2xl font-bold text-emerald-400">{totalParticipants}</p>
            <p className="text-[10px] text-[hsl(var(--text-muted))]">with points</p>
          </div>
          <div className="bg-[hsl(var(--card))] border border-blue-500/20 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1"><Zap size={14} className="text-blue-400" /><p className="text-xs text-blue-400">Total Points</p></div>
            <p className="text-2xl font-bold text-blue-400">{totalPoints.toLocaleString()}</p>
          </div>
        </div>

        {/* Podium #1/#2/#3 with names + avatars */}
        {top3.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mb-6" data-testid="podium">
            {[1, 0, 2].map((idx) => {
              const entry = top3[idx];
              if (!entry) return <div key={idx} />;
              const actualRank = idx + 1;
              const style = rankStyles[actualRank];
              const RankIcon = style.icon;
              const isMe = entry.user_id === currentUser.id;
              return (
                <div key={entry.user_id || idx}
                  className={`bg-gradient-to-b ${style.bg} border-2 ${style.border} rounded-xl p-5 text-center ${actualRank === 1 ? 'lg:-mt-4 lg:pb-8' : ''} relative transition-all hover:scale-[1.02]`}
                  data-testid={`podium-rank-${actualRank}`}>
                  <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${style.medal} mx-auto mb-2 flex items-center justify-center shadow-lg`}>
                    <RankIcon size={actualRank === 1 ? 26 : 20} className="text-white" />
                  </div>
                  <div className="flex justify-center mb-2">
                    <Avatar src={entry.avatar_url} name={entry.name} size={actualRank === 1 ? 56 : 44} glow={actualRank === 1} />
                  </div>
                  <p className={`text-2xl font-black ${style.text} mb-1`}>#{actualRank}</p>
                  <p className="font-bold text-sm text-white truncate" title={entry.name}>{entry.name}</p>
                  {entry.department && <p className="text-[10px] text-[hsl(var(--text-muted))]">{entry.department}</p>}
                  {isMe && <Badge className="mt-1 bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] text-[9px]">You</Badge>}
                  <div className={`inline-flex items-center gap-1 ${style.text} text-sm font-bold mt-2`}>
                    <Flame size={14} /> {entry.points || 0} pts
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Rest of Rankings */}
        {rest.length > 0 && (
          <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
            <div className="grid grid-cols-[50px_1fr_100px_120px] gap-2 p-3 bg-[hsl(var(--surface))] text-xs font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wider border-b border-[hsl(var(--border))]">
              <span className="text-center">Rank</span><span>Member</span><span className="text-center">Points</span><span className="text-right pr-2">Progress</span>
            </div>
            {rest.map((entry, idx) => {
              const rank = idx + 4;
              const pct = Math.round(((entry.points || 0) / maxPoints) * 100);
              const isMe = entry.user_id === currentUser.id;
              return (
                <div key={entry.user_id || idx}
                  className={`grid grid-cols-[50px_1fr_100px_120px] gap-2 p-3 items-center border-b border-[hsl(var(--border))] last:border-0 transition-colors ${isMe ? 'bg-[hsl(var(--primary))]/5' : 'hover:bg-[hsl(var(--surface))]'}`}
                  data-testid={`leaderboard-row-${rank}`}>
                  <span className="text-center font-bold text-[hsl(var(--text-muted))]">#{rank}</span>
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar src={entry.avatar_url} name={entry.name} size={32} />
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">
                        {entry.name}
                        {isMe && <Badge className="ml-1.5 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] text-[9px]">You</Badge>}
                      </p>
                      {entry.department && <p className="text-[10px] text-[hsl(var(--text-muted))] truncate">{entry.department}</p>}
                    </div>
                  </div>
                  <span className="text-center font-bold text-sm">{entry.points || 0}</span>
                  <div className="pr-2">
                    <div className="w-full h-2 bg-[hsl(var(--surface))] rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${pct > 70 ? 'bg-emerald-500' : pct > 40 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Zero-point members collapsed */}
        {zeroEntries.length > 0 && (
          <details className="mt-4 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
            <summary className="px-4 py-2.5 text-xs font-semibold text-[hsl(var(--text-muted))] cursor-pointer hover:text-white">
              Show {zeroEntries.length} members with 0 points
            </summary>
            <div className="px-4 pb-3 flex flex-wrap gap-2">
              {zeroEntries.map(e => (
                <div key={e.user_id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))]">
                  <Avatar src={e.avatar_url} name={e.name} size={18} />
                  <span className="text-[11px] text-[hsl(var(--text-muted))]">{e.name}</span>
                </div>
              ))}
            </div>
          </details>
        )}

        {top3.length === 0 && rest.length === 0 && (
          <div className="text-center py-16 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl">
            <Trophy size={48} className="mx-auto text-amber-500/30 mb-4" />
            <p className="text-[hsl(var(--text-secondary))] font-medium">No leaderboard data yet for {monthLabel}</p>
            <p className="text-sm text-[hsl(var(--text-muted))] mt-1">Start completing tasks and activities to earn points!</p>
          </div>
        )}
      </div>
    </ResponsiveLayout>
  );
}
